import { ApiError, errorKeyForSuiteApiResult, type ApiErrorKey } from './errors';
import { PortalClient, type UploadProgressCallback } from './portal-client';
import {
  ESuiteApiResult,
  type AdditionalOrderFile,
  type CreateOrderRequest,
  type DeliveryMethodsResult,
  type OrderFlowState,
  type OrderArticlePrice,
  type ProjectOrderDetails,
  type ProjectOrderListItem,
  type ReviewDesignRequest,
} from './types';

export interface GetOrderArticlesArgs {
  productionCenterId?: string;
  companyId?: string;
  contactId?: string;
  articleCategories?: readonly string[];
}

export type OrderFileCategory = 'OtherData' | 'ProtocolData';
export type InitialOrderFileCategory = 'ScannerData' | 'OrderData';

export interface PrepareAdditionalOrderFile {
  file: File;
  category: OrderFileCategory;
}

export interface PrepareAdditionalOrderFilesRequest {
  orderId: string;
  files: readonly PrepareAdditionalOrderFile[];
}

export interface DeleteAdditionalOrderFileRequest {
  orderId: string;
  fileId: string;
}

export interface DownloadOrderFileRequest {
  orderId: string;
  fileId: string;
}

export interface ReviewScanRevisionRequest {
  orderId: string;
  isAccepted: boolean;
  message: string;
}

export interface PrepareScanRevisionFilesRequest {
  orderId: string;
  files: readonly File[];
}

export interface CompleteScanRevisionRequest {
  orderId: string;
  fileIds: readonly string[];
}

function additionalFileErrorKey(result: ESuiteApiResult): ApiErrorKey {
  if (result === ESuiteApiResult.NotValid) {
    return 'fileRejected';
  }

  return errorKeyForSuiteApiResult(result);
}

async function hashFile(file: File): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * Orders API — lightweight order overview for MDenture.
 *
 * Endpoint:
 *   GET SuiteAPI/Order/GetProjectOrders
 * Auth:
 *   Authenticated (userName/userPWD or bearer token, plus brand headers)
 */
export class OrdersApi {
  private readonly client: PortalClient;

  public constructor(client: PortalClient) {
    this.client = client;
  }

  /**
   * Loads available order articles (including company prices) for the current scope.
   *
   * Lookup precedence mirrors the server contract:
   * - `contactId` takes precedence when set.
   * - Otherwise `productionCenterId + companyId` are used.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async getArticles(args: GetOrderArticlesArgs, signal?: AbortSignal): Promise<OrderArticlePrice[]> {
    const query: Record<string, string> = {};

    const contactId = args.contactId?.trim();
    const productionCenterId = args.productionCenterId?.trim();
    const companyId = args.companyId?.trim();
    const categories = (args.articleCategories ?? [])
      .map((category) => category.trim())
      .filter((category) => category.length > 0);

    if (contactId) {
      query.contactId = contactId;
    } else {
      if (productionCenterId) {
        query.productionCenterId = productionCenterId;
      }
      if (companyId) {
        query.companyId = companyId;
      }
    }

    if (categories.length > 0) {
      query.articleCategories = Array.from(new Set(categories)).join(',');
    }

    const result = await this.client.request<OrderArticlePrice[]>({
      method: 'GET',
      path: '/SuiteAPI/Order/Articles',
      authenticated: true,
      query,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data ?? [];
  }

  /**
   * Loads the order overview list for the authenticated user's organization.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async getProjectOrders(signal?: AbortSignal): Promise<ProjectOrderListItem[]> {
    const result = await this.client.request<ProjectOrderListItem[]>({
      method: 'GET',
      path: '/SuiteAPI/Order/GetProjectOrders',
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data ?? [];
  }

  /**
   * Loads the configured order-flow states, including group-specific overrides.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async getFlowStates(languageId: string, signal?: AbortSignal): Promise<OrderFlowState[]> {
    const result = await this.client.request<OrderFlowState[]>({
      method: 'PUT',
      path: '/SuiteAPI/Order/FlowStatesV1',
      authenticated: true,
      body: { languageID: languageId.trim() },
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data ?? [];
  }

  /**
   * Loads details for one order scoped to the authenticated user.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async getProjectOrder(orderId: string, signal?: AbortSignal): Promise<ProjectOrderDetails> {
    const result = await this.client.request<ProjectOrderDetails>({
      method: 'GET',
      path: `/SuiteAPI/Order/GetProjectOrder?orderID=${encodeURIComponent(orderId)}`,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }

  /**
   * Cancels an eligible MDenture order without deleting its data.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async cancelOrder(orderId: string, signal?: AbortSignal): Promise<void> {
    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: `/SuiteAPI/Order/CancelOrderDirect?orderId=${encodeURIComponent(orderId)}`,
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
  }

  /**
   * Submits the authenticated customer's design-review decision for an order.
   *
   * The Portal validates the current order state, customer ownership and rejection reason.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async reviewDesign(
    request: { orderId: string; isAccepted: boolean; rejectionReason?: string },
    signal?: AbortSignal,
  ): Promise<void> {
    const body: ReviewDesignRequest = {
      orderID: request.orderId,
      isAccepted: request.isAccepted,
      ...(request.rejectionReason !== undefined ? { rejectionReason: request.rejectionReason.trim() } : {}),
    };
    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: '/SuiteAPI/Order/ReviewDesignV1',
      authenticated: true,
      body,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
  }

  /**
   * Submits the authenticated customer's scan-revision decision for an order.
   */
  public async reviewScanRevision(request: ReviewScanRevisionRequest, signal?: AbortSignal): Promise<void> {
    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: '/SuiteAPI/Order/ReviewScanRevisionV1',
      authenticated: true,
      body: {
        orderID: request.orderId,
        isAccepted: request.isAccepted,
        message: request.message.trim(),
      },
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
  }

  /**
   * Reserves same-name project-file revisions for a replacement scan upload.
   */
  public async prepareScanRevisionFiles(
    request: PrepareScanRevisionFilesRequest,
    signal?: AbortSignal,
  ): Promise<AdditionalOrderFile[]> {
    const files: Array<{ fileName: string; hash: string }> = [];
    for (const file of request.files) {
      files.push({ fileName: file.name, hash: await hashFile(file) });
    }

    const result = await this.client.request<AdditionalOrderFile[]>({
      method: 'PUT',
      path: '/SuiteAPI/Order/PrepareScanRevisionUploadsV1',
      authenticated: true,
      body: {
        orderID: request.orderId,
        files,
      },
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', additionalFileErrorKey(serverResult), { serverResult });
    }

    return result.data ?? [];
  }

  /**
   * Completes a replacement scan upload and moves the order to To Confirm.
   */
  public async completeScanRevision(
    request: CompleteScanRevisionRequest,
    signal?: AbortSignal,
  ): Promise<void> {
    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: '/SuiteAPI/Order/CompleteScanRevisionV1',
      authenticated: true,
      body: {
        orderID: request.orderId,
        fileIDs: [...request.fileIds],
      },
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
  }

  /**
   * Returns available delivery methods and prices for the given production center and customer.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async getDeliveryMethods(
    productionCenterId: string,
    companyId: string,
    signal?: AbortSignal,
  ): Promise<DeliveryMethodsResult> {
    const result = await this.client.request<DeliveryMethodsResult>({
      method: 'GET',
      path: '/SuiteAPI/Order/DeliveryMethods',
      authenticated: true,
      query: { productionCenterId, companyId },
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }

  /**
   * Uploads a categorized file to an order created through MDenture. Mill production files are
   * bound to the pending CAD relation by filename; other files are attached as additional files.
   *
   * @returns The ID of the newly created file record.
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async uploadScanFile(
    orderId: string,
    file: File,
    category: InitialOrderFileCategory,
    signal?: AbortSignal,
    onProgress?: UploadProgressCallback,
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const result = await this.client.uploadFormData<string>({
      path: '/SuiteAPI/Order/UploadScanFile',
      query: { orderId, category },
      formData,
      authenticated: true,
      ...(signal ? { signal } : {}),
      ...(onProgress ? { onProgress } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }

  /**
   * Reserves pending file records for an additional upload batch.
   *
   * The server may return a conflict-resolved filename, so callers must use
   * the returned IDs and names for the subsequent upload requests.
   */
  public async prepareAdditionalOrderFiles(
    request: PrepareAdditionalOrderFilesRequest,
    signal?: AbortSignal,
  ): Promise<AdditionalOrderFile[]> {
    const files = [];
    for (const item of request.files) {
      const hash = await hashFile(item.file);
      files.push({ fileName: item.file.name, category: item.category, hash });
    }

    const result = await this.client.request<AdditionalOrderFile[]>({
      method: 'PUT',
      path: '/SuiteAPI/Order/PrepareOrderFileUploads',
      authenticated: true,
      body: {
        orderID: request.orderId,
        files,
      },
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', additionalFileErrorKey(serverResult), { serverResult });
    }

    return result.data ?? [];
  }

  /**
   * Uploads one file reserved by {@link prepareAdditionalOrderFiles}.
   */
  public async uploadAdditionalOrderFile(
    fileId: string,
    file: File,
    options: {
      sendNotification: boolean;
      message?: string;
      language?: string;
      onProgress?: UploadProgressCallback;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('FileId', fileId);
    formData.append('SendNotification', String(options.sendNotification));
    if (options.language?.trim()) {
      formData.append('Language', options.language.trim());
    }
    if (options.message?.trim()) {
      formData.append('Message', options.message.trim());
    }

    const result = await this.client.uploadFormData<boolean>({
      method: 'POST',
      path: '/SuiteAPI/Order/UploadOrderFile',
      formData,
      authenticated: true,
      ...(signal ? { signal } : {}),
      ...(options.onProgress ? { onProgress: options.onProgress } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', additionalFileErrorKey(serverResult), { serverResult });
    }
  }

  /**
   * Deletes one additional file from an order.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async deleteAdditionalOrderFile(
    request: DeleteAdditionalOrderFileRequest,
    signal?: AbortSignal,
  ): Promise<void> {
    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: '/SuiteAPI/Order/DeleteOrderFile',
      authenticated: true,
      body: {
        orderID: request.orderId,
        fileID: request.fileId,
      },
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', additionalFileErrorKey(serverResult), { serverResult });
    }
  }

  /** Downloads one file belonging to an order as a browser blob. */
  public async downloadOrderFile(request: DownloadOrderFileRequest, signal?: AbortSignal): Promise<Blob> {
    return this.client.download({
      method: 'GET',
      path: '/SuiteAPI/Order/DownloadOrderFile',
      authenticated: true,
      query: { orderID: request.orderId, fileID: request.fileId },
      ...(signal ? { signal } : {}),
    });
  }

  /**
   * Places a new MDenture order with the selected design articles.
   *
   * @returns The newly created order ID (Guid string).
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async createOrder(request: CreateOrderRequest, signal?: AbortSignal): Promise<string> {
    const result = await this.client.request<string>({
      method: 'PUT',
      path: '/SuiteAPI/Order/CreateOrder',
      authenticated: true,
      body: request,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }

  /**
   * Places a new MDenture order by creating the DB entities directly from the selected product IDs.
   *
   * @returns The newly created order ID (Guid string).
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async createDirectOrder(request: CreateOrderRequest, signal?: AbortSignal): Promise<string> {
    const result = await this.client.request<string>({
      method: 'PUT',
      path: '/SuiteAPI/Order/CreateOrderDirect',
      authenticated: true,
      body: request,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }
}
